import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { invoke, convertFileSrc } from "./tauri-web";
import { listen } from "./tauri-web";
import { getCurrentWebview } from "./tauri-web";
import { openFileDialog, saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { platform } from "./tauri-web";
import { supabase, getAccessToken } from "./supabase";
import { store, applyFilter, DEFAULT_STATUS_LABELS, loadStatusLabels, saveStatusLabels, type Category, type Expense, type ExpenseStatus, type AuditEntry, type ExpenseFilter, type BudgetStatus, type MonthTotal } from "./store";
import { PhotoViewer, type ViewerPhoto } from "./PhotoViewer";

/** PhotoViewer, plus the one thing this app needs around it: while a receipt is open full screen,
 *  the floating "Chat" launcher must get out of the way — it sat on top of the viewer's rotate
 *  button (and of "Save expense" further down the page). PhotoViewer.tsx is copied verbatim across
 *  the fleet, so the behaviour is added HERE rather than by editing that shared file. */
function ReceiptViewer(props: { photos: ViewerPhoto[]; startIndex?: number; onClose: () => void }) {
  useEffect(() => {
    document.body.classList.add("viewer-open");
    return () => document.body.classList.remove("viewer-open");
  }, []);
  return <PhotoViewer {...props} />;
}
import {
  structureReceipt, suggestCategory, parseExpenseQuery, monthSummary, findAnomalies,
  openWebLogin, BrainError, type Anomaly,
} from "./brain";
import { buildExport, type ExportFormat } from "./export";
import { BackupMenu } from "./BackupMenu";
import { WalletTopUp } from "./iap";
import { AddFundsMenu } from "./AddFundsMenu";
import { OrioraBadge } from "./OrioraBadge";
import Chat from "./chat/Chat";
import { Lock, Sparkles, Bot, Camera, Check, X, History, Trash2, AlertTriangle, Search, WifiOff, Pencil } from "lucide-react";
import glyphCapture from "./assets/glyph-capture.svg";
import momentEmptyLedger from "./assets/moment-empty-ledger.svg";

// Wallet billing is native to Oriora: AI runs through the user's Oriora account, billed to their
// central Oriora wallet (server-side, via runStepWithBilling). The app shows no balance/model UI.
// Top-up is dual-MoR: on the WEB via Creem, and on iOS via Apple IAP (StoreKit → the SAME central
// wallet; see iap.tsx) — the IAP grid shows credit AMOUNTS only, never the Apple price (price-rule).
// A 402 from the brain still surfaces as "add funds in your Oriora account" with no figure.
const ACCOUNT_URL = "https://orioralabs.com/settings";

const money = (c: number, currency = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency, currencyDisplay: "narrowSymbol" }).format(c / 100);
// Selectable currency (review finding: "cannot change currency in-app"). The DEFAULT applies to new
// expenses; every expense keeps its own currency and totals GROUP per currency — mixed-currency
// logs are never summed into one fake number. All math is local.
const CURRENCIES = ["USD", "EUR", "GBP", "SGD", "AUD", "CAD", "JPY", "CNY", "INR", "CHF", "SEK", "NOK", "DKK", "PLN", "RON", "AED"];
const CURRENCY_KEY = "oriora.expense.currency";
const getDefaultCurrency = () => { try { return localStorage.getItem(CURRENCY_KEY) || "USD"; } catch { return "USD"; } };
const moneyByCurrency = (list: { amount_cents: number; currency: string }[]) => {
  const g = new Map<string, number>();
  for (const e of list) g.set(e.currency || "USD", (g.get(e.currency || "USD") ?? 0) + e.amount_cents);
  if (g.size === 0) return money(0, getDefaultCurrency());
  return [...g.entries()].sort((a, b) => b[1] - a[1]).map(([c, v]) => money(v, c)).join(" · ");
};
// Labels live in store.ts so the ledger, the chips and the web runtime can't drift apart.
// Stored values stay pending/approved/rejected — only the words a person reads change, and
// they can rewrite those words themselves (Categories panel).
function useStatusLabels(): Record<ExpenseStatus, string> {
  const [labels, setLabels] = useState(loadStatusLabels);
  useEffect(() => {
    const sync = () => setLabels(loadStatusLabels());
    window.addEventListener("oriora-status-labels-changed", sync);
    window.addEventListener("storage", sync); // another tab/window edited them
    return () => {
      window.removeEventListener("oriora-status-labels-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return labels;
}
const LIST_CAP = 50000; // raised from 1000 so years of receipts don't silently truncate from totals/search
const fmtDate = (s: string) => (s ? new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) : "");
const todayISO = () => new Date().toISOString().slice(0, 10);

// AI-data consent (App Store Guideline 5.1.1(i)/5.1.2): a one-time disclosure shown at first
// launch, before the app is usable, so the user agrees before any data is sent to an AI provider.
// Bump the version suffix to re-prompt everyone if the disclosure materially changes.
const AI_CONSENT_KEY = "oriora.ai_consent.v1";

// Sim/screenshot rig: 1 = bypass consent+login so shot runs can reach every screen.
// ⚠️ MUST be 0 in any shipped build (per-app ledger checks this).
const SCREENSHOT = 0;

/**
 * 'photograph the receipt, the ledger audits itself' — a docket being checked over.
 * Replaces the lucide `Sparkles` that 20 of the fleet's 26 chat launchers rendered in common;
 * the button outline changes with it, because one glyph in two colours is still one control
 * (Guideline 4.3(a)).
 */
function DocketMark({ size = 19 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M7 8h7M7 12h7M7 16h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="16.5" cy="16" r="3.1" stroke="currentColor" strokeWidth="1.5" />
      <path d="m18.8 18.3 2.2 2.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);

  // AI-data consent gate (first launch). Read synchronously from localStorage so an already-
  // consented user never sees a flash of the consent screen on relaunch.
  const [consented, setConsented] = useState(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });
  const acceptConsent = useCallback(() => {
    try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* ignore — still consented for this session */ }
    setConsented(true);
  }, []);

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground. THIS is the reliable catch for
  // "deleted my account, came back, still signed in": returning to the app (closing the
  // in-app delete window, or re-opening from the app switcher) re-checks the token, the
  // server rejects the deleted account, and we drop to login — no dependence on the
  // deep-link event, which has proven flaky on device.
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns expense-audit-ai://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    async function applyDeepLinkUrl(raw: string) {
      const u = new URL(raw);
      const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
      const access_token = frag.get("access_token");
      const refresh_token = frag.get("refresh_token");
      if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
    }
    listen<string>("auth:deep-link", async (e) => {
      try { await applyDeepLinkUrl(e.payload); }
      catch (err) { console.error("deep-link auth failed", err); }
    }).then(async (u) => {
      unlisten = u;
      // Drain any URL buffered in Rust before this listener registered (cold-start race).
      try {
        const pending = await invoke<string | null>("take_pending_deep_link");
        if (pending) await applyDeepLinkUrl(pending);
      } catch { /* ignore — non-fatal */ }
    });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // expense-audit-ai://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      // Clear the on-device session WITHOUT a server /logout (the account is gone → global
      // signOut errors). Then FORCE the logged-out UI directly — onAuthStateChange/SIGNED_OUT
      // is unreliable with the async Tauri file storage, so don't depend on it flipping `authed`.
      try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  if (SCREENSHOT) return <Dashboard email="you@oriora.app" onSignedOut={() => {}} />;
  if (!mounted) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!authed) return <Login />;
  // Guideline 5.1.1(i)/5.1.2: disclose + obtain consent before any AI data is sent. AI calls need a
  // session, so we show this ONCE right after sign-in, before the dashboard — the earliest point
  // where consent is meaningful (nothing is ever sent pre-login). Persisted, so never shown again.
  if (!consented) return <AiConsent onAccept={acceptConsent} />;
  // onSignedOut lets the Dashboard's Sign-out button flip App-owned auth state directly —
  // onAuthStateChange/SIGNED_OUT is unreliable with the async Tauri file storage.
  return <Dashboard email={email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

// AI-data consent screen (Guideline 5.1.1(i)/5.1.2). Dry, minimal disclosure: names the data
// sent + the providers + the purpose, links the Privacy Policy, requires "I Agree". Shown once.
function AiConsent({ onAccept }: { onAccept: () => void }) {
  const openPrivacy = () => { void invoke("open_external", { url: "https://orioralabs.com/about/privacy" }); };
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.25rem, env(safe-area-inset-top)) 1.25rem max(1.25rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", margin: "auto" }}>
        <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)", textAlign: "center" }}>Oriora Expense</div>
        <h1 style={{ fontSize: "1.4rem", margin: "0.5rem 0 0.85rem", textAlign: "center" }}>How Oriora uses AI</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6, marginBottom: "0.85rem" }}>
          When you use an AI feature, Oriora Expense sends the data for that action — receipt images
          and expense text — to third-party AI providers (Anthropic, OpenAI, Google, and others) to
          read receipts, suggest categories, and answer your questions.
        </p>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6, marginBottom: "1.25rem" }}>
          Your data stays yours — we don't retain it: data passes through Oriora for each AI action and isn't kept by us. You choose where it lives — this device, and optionally your own GitHub or Google Drive. By
          continuing, you agree to this.
        </p>
        <button className="btn btn-primary" style={{ width: "100%", marginBottom: "0.6rem" }} onClick={onAccept}>I Agree</button>
        <button className="btn-ghost" style={{ width: "100%" }} onClick={openPrivacy}>Privacy Policy</button>
      </div>
    </main>
  );
}

// iOS sign-in: present the Oriora login page inside ASWebAuthenticationSession — an in-app Safari
// sheet that Google/GitHub accept AND Apple requires (Guideline 4: no external Safari for auth). The
// whole login page runs in it (email-code, Google, GitHub, Apple); the final
// expense-audit-ai://auth#tokens redirect is captured by the session and returned here.
// macOS desktop uses the SAME system-sheet API via signInMac (open_native_auth_session) so the whole
// OAuth flow stays in-app too — replacing the old open_login_window, which kicked Google/GitHub OAuth
// out to the external browser. Plugin (iOS) = tauri-plugin-web-auth (audited, pinned).
const APP_SCHEME = "expense-audit-ai";

// Turn a captured `expense-audit-ai://auth#access_token=…&refresh_token=…` callback URL into a session.
async function applyAuthCallback(callbackUrl: string): Promise<void> {
  const frag = new URLSearchParams(new URL(callbackUrl).hash.replace(/^#/, ""));
  const access_token = frag.get("access_token");
  const refresh_token = frag.get("refresh_token");
  if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
}

async function signInIos(): Promise<void> {
  const { callbackUrl } = await invoke<{ callbackUrl: string }>("plugin:web-auth|authenticate", {
    payload: { url: `https://orioralabs.com/login?return=${APP_SCHEME}`, callbackScheme: APP_SCHEME },
  });
  await applyAuthCallback(callbackUrl);
}

// §2 (#61, Guideline 4) — macOS sign-in in the app's OWN WebKit window (open_login_window, ipc.rs).
// NOT ASWebAuthenticationSession: on a Mac whose default browser is Chrome, macOS hands that session
// to the browser — Apple rejected three sibling macOS builds for it (Guideline 4, 2026-09-08: "the user
// is taken to the default web browser to sign in"), reproduced live 2026-09-11 (ephemeral AND non-ephemeral).
// The in-app window (`https://orioralabs.com/login?return=expense-audit-ai`) hands the session back via
// `auth:deep-link` (App.tsx listener) and closes itself — proven end-to-end on Analyst 2026-09-12:
// Turnstile solved in-window, email code accepted, app signed in; Apple approved all three 2026-09-21.
async function signInMac(): Promise<void> {
  await invoke("open_login_window");
}

// iOS account deletion — MIRRORS signInIos. Opens the delete page in the in-app
// ASWebAuthenticationSession (NOT the external browser), so the session never lands in the phone's
// browser history. The page deletes the account, then redirects to expense-audit-ai://deleted
// (captured by the auth session); we sign out locally afterward.
async function deleteAccountIos(accessToken: string, refreshToken: string): Promise<void> {
  await invoke<{ callbackUrl: string }>("plugin:web-auth|authenticate", {
    payload: {
      url: `https://orioralabs.com/account/delete?return=${APP_SCHEME}#access_token=${accessToken}&refresh_token=${refreshToken}`,
      callbackScheme: APP_SCHEME,
    },
  });
  await supabase.auth.signOut();
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.25rem, env(safe-area-inset-top)) 1.25rem max(1.25rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", textAlign: "center" }}>
        <h1 style={{ fontSize: "var(--fs-2xl)", margin: "0 0 0.35rem" }}>Oriora Expense</h1>
        <p className="muted" style={{ fontFamily: "var(--serif)", fontStyle: "italic", fontSize: "var(--fs-sm)", margin: "0 0 1rem" }}>Photograph the receipt. The ledger audits itself.</p>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginBottom: "1.25rem", textAlign: "left" }}>
          Uses your Oriora account — sign in, then you're back here. Your expenses live on this
          device; AI runs through your Oriora account. Oriora doesn't retain what you send for an AI action. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // Every surface signs in fully in-app (Apple Guideline 4 — no external browser):
            // iOS + macOS → ASWebAuthenticationSession system sheet; other desktops → in-app window fallback.
            try {
              const p = platform();
              if (p === "ios") await signInIos();
              else if (p === "macos") await signInMac();
              else if (p === "web") window.location.href = "/login?next=/expense-audit-ai/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
      </div>
    </main>
  );
}

function Dashboard({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  // Account panel: Sign out + Delete live INSIDE it (opened from the email chip), separated, so
  // Delete can never be mis-tapped next to Sign out. Delete is also two-step confirm-guarded.
  const [accountOpen, setAccountOpen] = useState(false);
  const [showMgmt, setShowMgmt] = useState(false);
  const statusLabels = useStatusLabels();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState<Expense | null>(null);
  const [showCats, setShowCats] = useState(false);
  // Anomaly ids lifted out of Insights so flagged rows can be badged in the ledger.
  const [anomalyIds, setAnomalyIds] = useState<Set<string>>(new Set());
  // NL-search filter lifted from AskExpenses so it filters the main ledger (composes with statusFilter).
  const [searchFilter, setSearchFilter] = useState<{ filter: ExpenseFilter; label: string } | null>(null);
  // Export-scope confirm: which set to write (null = closed).
  const [exportScope, setExportScope] = useState<ExportFormat | null>(null);
  // Offline banner — entries still persist to SQLite; only the AI calls pause.
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — does not change
  // any existing screen. onChanged → reload() refreshes the ledger after a chat-driven mutation.
  const [chatOpen, setChatOpen] = useState(false);
  // Native-menu targets: scroll/focus the Add-expense form, focus the Ask input.
  const addFormRef = useRef<HTMLDivElement>(null);
  const askInputRef = useRef<HTMLInputElement>(null);

  const monthStart = useMemo(() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); }, []);

  // OFFLINE BANNER: track connectivity (additive — does not touch any FROZEN listener).
  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // MENU-EVENT LISTENER: route native menu actions to existing state/refs.
  // Separate from (and does not disturb) the FROZEN "auth:deep-link" listener.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "add":
          addFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          addFormRef.current?.querySelector<HTMLInputElement>("input")?.focus();
          break;
        case "search":
          askInputRef.current?.focus();
          askInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          break;
        case "export":
          setExportScope("generic");
          break;
        case "categories":
          setShowCats((v) => !v);
          break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const reload = useCallback(async () => {
    const [c, e] = await Promise.all([store.listCategories(), store.listExpenses({ limit: LIST_CAP })]);
    setCategories(c); setExpenses(e);
  }, []);

  useEffect(() => {
    void store.ensureSeed()
      .then(() => store.materializeRecurring(todayISO()).catch(() => 0)) // add any due recurring expenses
      .then(reload);
  }, [reload]);

  const monthExpenses = useMemo(() => expenses.filter((e) => e.date_on >= monthStart), [expenses, monthStart]);
  const [defaultCurrency, _setDefaultCurrency] = useState(getDefaultCurrency());
  useEffect(() => { // chat can change it ("set default currency EUR") — keep the header select in sync
    const h = () => _setDefaultCurrency(getDefaultCurrency());
    window.addEventListener("oriora:currency-changed", h);
    return () => window.removeEventListener("oriora:currency-changed", h);
  }, []);
  const setDefaultCurrency = (c: string) => { _setDefaultCurrency(c); try { localStorage.setItem(CURRENCY_KEY, c); } catch { /* ignore */ } };
  const filtered = useMemo(() => {
    let rows = expenses.filter((e) => !statusFilter || e.status === statusFilter);
    if (searchFilter) rows = applyFilter(rows, searchFilter.filter);
    return rows;
  }, [expenses, statusFilter, searchFilter]);

  // G21 — the receipt is the evidence. Tapping one opens it full-screen (pinch, rotate, ‹ ›),
  // and ‹ › walks every receipt in the CURRENT view, which is how you actually audit: flip
  // through the month's evidence against the amounts. Built from `filtered` so the filter you
  // are looking at is the set you page through.
  // Was `open_external`, which hands the file to Preview on a Mac and does NOTHING on iPhone —
  // so on the phone the receipt thumbnail was simply dead.
  const receiptRows = useMemo(() => filtered.filter((e) => e.receipt_path), [filtered]);
  const receiptPhotos = useMemo<ViewerPhoto[]>(
    () => receiptRows.map((e) => ({
      src: convertFileSrc(e.receipt_path!),
      alt: `Receipt — ${e.vendor}, ${e.date_on}`,
    })),
    [receiptRows],
  );
  const [receiptAt, setReceiptAt] = useState<number | null>(null);

  function writeExport(format: ExportFormat, set: Expense[]) {
    const { csv, filename } = buildExport(format, set);
    void (async () => {
      const path = await saveFileDialog({ defaultPath: filename });
      if (path) await writeTextFile(path, csv);
    })();
  }

  const monthLabel = useMemo(() => new Date(monthStart + "T00:00:00Z").toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" }), [monthStart]);

  return (
    <main className="app-main">
      {offline && (
        <div role="status" className="notice notice-offline">
          <WifiOff size={14} style={{ flexShrink: 0 }} /> AI paused — entries still save locally
        </div>
      )}
      {/* The masthead — a ruled page header. The month figure moved DOWN into the review layer
          (R3); the head carries only the book's name and which month's page is open. */}
      <header className="masthead">
        <div>
          <h1>Oriora Expense</h1>
          <span className="month-label">{monthLabel}</span>
        </div>
        <div className="row" style={{ gap: "0.5rem", alignItems: "center", flexWrap: "nowrap" }}>
        <BackupMenu
          appName="Expense" fileSlug="expense" appScheme="expense-audit-ai"
          hasData={expenses.length > 0} onRestored={reload}
        />
        <div style={{ position: "relative" }}>
          <button onClick={() => { setAccountOpen((o) => !o); setShowMgmt(false); setConfirmDelete(false); }}
            aria-label="Account" aria-expanded={accountOpen}
            style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--accent)", color: "#fff", display: "grid", placeItems: "center", fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer" }}>
            {(email?.[0] ?? "?").toUpperCase()}
          </button>
          {accountOpen && (
            <>
              <div onClick={() => { setAccountOpen(false); setShowMgmt(false); setConfirmDelete(false); }} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
              <div className="card" style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 50, width: 240, padding: "0.85rem", textAlign: "left", maxHeight: "75vh", overflowY: "auto" }}>
                <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: 2 }}>Signed in as</div>
                <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600, marginBottom: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email}</div>
                <button className="btn" style={{ width: "100%", fontSize: "var(--fs-xs)", marginBottom: 6 }}
                  onClick={async () => { try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* ignore */ } onSignedOut(); }}>Sign out</button>
                {!showMgmt ? (
                  <button onClick={() => setShowMgmt(true)}
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: "var(--fs-xs)", color: "var(--muted, #8a8a8a)" }}>Account management…</button>
                ) : !confirmDelete ? (
                  <div style={{ marginTop: 6 }}>
                    <button onClick={() => setConfirmDelete(true)}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: "var(--fs-xs)", color: "#dc2626" }}>Delete account…</button>
                    <span style={{ marginLeft: 10, fontSize: "var(--fs-xs)", color: "var(--muted, #8a8a8a)", cursor: "pointer" }} onClick={() => setShowMgmt(false)}>← back</span>
                  </div>
                ) : (
                  <div style={{ marginTop: 6 }}>
                    <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: 8, lineHeight: 1.45 }}>This permanently deletes your Oriora account. It can&apos;t be undone.</div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button style={{ flex: 1, fontSize: "var(--fs-xs)", fontWeight: 600, background: "#dc2626", color: "#fff", border: "none", borderRadius: 8, padding: "0.45rem", cursor: "pointer" }}
                        onClick={async () => {
                          const { data } = await supabase.auth.getSession();
                          if (data.session) {
                            // iOS deletes via in-app web-auth (mirrors login — no external browser);
                            // desktop opens the in-app WebKit window.
                            if (platform() === "ios") {
                              await deleteAccountIos(data.session.access_token, data.session.refresh_token);
                            } else {
                              await invoke("open_delete_window", { accessToken: data.session.access_token, refreshToken: data.session.refresh_token });
                            }
                          } else {
                            await invoke("open_external", { url: "https://orioralabs.com/account/delete" });
                          }
                        }}>Delete</button>
                      <button className="btn" style={{ flex: 1, fontSize: "var(--fs-xs)" }} onClick={() => { setConfirmDelete(false); setShowMgmt(false); }}>Cancel</button>
                    </div>
                  </div>
                )}
                {/* Wallet top-up. iOS + Mac App Store: Apple IAP (StoreKit) credits the central Oriora
                    wallet — credit amounts shown, never the Apple price (price-rule). The MAS build is
                    identified by VITE_MAS=1 baked at build time; the Developer-ID (DMG) build has no
                    StoreKit, so it keeps the web link. Other desktop: funds added on the web. */}
                {platform() === "ios" || (platform() === "macos" && (import.meta as any).env?.VITE_MAS === "1") ? (
                  <WalletTopUp />
                ) : (
                  <div style={{ marginTop: "0.85rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border)" }}>
                    <button
                      onClick={() => void invoke("open_external", { url: ACCOUNT_URL })}
                      className="btn" style={{ width: "100%", fontSize: "var(--fs-xs)", fontWeight: 600 }}>
                      Manage your Oriora account →
                    </button>
                    <div className="muted" style={{ fontSize: "10px", marginTop: 6, lineHeight: 1.4 }}>
                      AI runs through your Oriora account. Add funds in your Oriora account on the web.
                    </div>
                  </div>
                )}
                <div style={{ marginTop: "0.85rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border)" }}>
                  <label className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.3rem", marginBottom: "0.6rem" }}>Default currency
                    <select aria-label="Default currency" value={defaultCurrency} onChange={(e) => setDefaultCurrency(e.target.value)} style={{ width: "auto", fontSize: "var(--fs-xs)", padding: "0.1rem 0.3rem" }}>
                      {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                  <button
                    onClick={() => void invoke("open_external", { url: "https://orioralabs.com/apps/expense-audit-ai" })}
                    className="btn" style={{ width: "100%", fontSize: "var(--fs-xs)", fontWeight: 600 }}>
                    How to use Oriora Expense →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        {/* Add funds is ALSO reachable here, one tap away, not only nested inside the account
            avatar's popover above — Apple 2.1(b) rejected Focus/Gifted v1.0 when the wallet
            top-up lived only inside that nested popover (2026-09-05). Same fix shape. */}
        <AddFundsMenu />
        </div>
      </header>

      {/* R1 — CAPTURE LEADS. The first affordance on the page is photographing a receipt. */}
      <div ref={addFormRef}>
        <AddExpense categories={categories} editing={editing} onDone={() => { setEditing(null); void reload(); }} onCancel={() => setEditing(null)} />
      </div>

      {/* R2 — THE LEDGER IS THE IDENTITY: the book, with its own head, filters and search. */}
      <section>
        <div className="book-head" style={{ flexWrap: "wrap" }}>
          <h2 className="display" style={{ fontSize: "var(--fs-xl)", margin: 0, whiteSpace: "nowrap" }}>The ledger</h2>
          <div className="row" style={{ gap: "0.35rem", flexWrap: "wrap", justifyContent: "flex-end", flex: "1 1 auto" }}>
            <button className="btn btn-sm" title="QuickBooks Online import format" onClick={() => setExportScope("quickbooks")}>QuickBooks</button>
            <button className="btn btn-sm" title="Xero bank-statement import format" onClick={() => setExportScope("xero")}>Xero</button>
            <button className="btn btn-sm" title="Full CSV" onClick={() => setExportScope("generic")}>CSV</button>
            <button className="btn btn-sm" onClick={() => setShowCats((v) => !v)}>Categories</button>
          </div>
        </div>

        <div className="filterbar">
          <div className="seg" role="group" aria-label="Filter by status">
            {([["", "All"], ["pending", statusLabels.pending], ["approved", statusLabels.approved], ["rejected", statusLabels.rejected]] as const).map(([v, label]) => (
              <button key={v || "all"} aria-pressed={statusFilter === v} onClick={() => setStatusFilter(v)}>{label}</button>
            ))}
          </div>
        </div>

        {/* R4 — Ask is the book's own search field. */}
        <AskExpenses askInputRef={askInputRef} categories={categories} expenses={expenses} onApplyFilter={(filter, label) => setSearchFilter({ filter, label })} />

        {searchFilter && (
          <div className="row" style={{ margin: "0.5rem 0" }}>
            <span className="chip chip-accent">
              <Search size={12} /> {searchFilter.label}
              <button className="btn" style={{ border: "none", background: "none", padding: 0, color: "var(--accent)", display: "inline-flex", alignItems: "center", boxShadow: "none" }} title="Clear search" aria-label="Clear search" onClick={() => setSearchFilter(null)}><X size={12} /></button>
            </span>
          </div>
        )}

        {showCats && <Categories categories={categories} onChange={reload} />}

        <WhereItWent monthStart={monthStart} monthLabel={monthLabel} reloadKey={expenses.length} />
        <Budgets categories={categories} monthStart={monthStart} monthLabel={monthLabel} reloadKey={expenses.length} />

        {expenses.length >= LIST_CAP && (
          <div className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0.5rem 0" }}>
            Showing the most recent {LIST_CAP.toLocaleString()} expenses — narrow by filter or date to see older items.
          </div>
        )}

        {filtered.length === 0 ? (
          expenses.length === 0 ? (
            <EmptyBook categories={categories} defaultCurrency={defaultCurrency} onAdded={reload} />
          ) : (
            <div className="card muted" style={{ padding: "2rem", textAlign: "center" }}>No expenses match the current filter.</div>
          )
        ) : (
          <>
            <div className="book-head" style={{ marginTop: "0.75rem" }}>
              <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>
                {searchFilter || statusFilter ? `${filtered.length} of ${expenses.length} entries` : `Showing all ${expenses.length}`}
              </span>
              <span className="book-total">{moneyByCurrency(filtered)}</span>
            </div>
            <div className="ledger-card" style={{ overflowX: "auto" }}>
              <table className="ledger-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Date</th>
                    <th>Category</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th>Status</th>
                    <th style={{ width: 1 }} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => (
                    <ExpenseRow
                      key={e.id}
                      e={e}
                      flagged={anomalyIds.has(e.id)}
                      onEdit={() => setEditing(e)}
                      onChange={reload}
                      onViewReceipt={() => setReceiptAt(receiptRows.findIndex((r) => r.id === e.id))}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* R3 — THE MONTH'S POSITION: the review layer, folded below the book. */}
      <Insights expenses={monthExpenses} categories={categories} monthLabel={monthLabel} onAnomalies={(a) => setAnomalyIds(new Set(a.map((x) => x.expense_id)))} monthStart={monthStart} />

      {receiptAt !== null && receiptAt >= 0 && receiptPhotos.length > 0 && (
        <ReceiptViewer photos={receiptPhotos} startIndex={receiptAt} onClose={() => setReceiptAt(null)} />
      )}

      {exportScope && (
        <ExportScopeDialog
          format={exportScope}
          filtered={filtered}
          monthExpenses={monthExpenses}
          allExpenses={expenses}
          onCancel={() => setExportScope(null)}
          onChoose={(set) => { writeExport(exportScope, set); setExportScope(null); }}
        />
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the ledger. */}
      {!chatOpen && (
        <button className="ecp-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
          <DocketMark size={19} />
        </button>
      )}
      <aside className={`ecp-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="ecp-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to add, find, or summarize</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>

    </main>
  );
}

// ── THE BOOK, DRAWN — the audit is what this app is, so it is shown before the first entry ──
// PARITY-MATRIX rows 9 and 10 are the two the leaders do not have (duplicate/anomaly flags and a
// change history), and at zero entries neither was visible: the ledger was one art card reading
// "The book is empty". The marks are the app's own — ‡ duplicate, † unusual, ? unsure — drawn on
// the real ruled table so the checks are legible before there is anything to check.
function EmptyBook({ categories, defaultCurrency, onAdded }: {
  categories: Category[]; defaultCurrency: string; onAdded: () => void;
}) {
  const marks: { cls: string; head: string; sub: string }[] = [
    { cls: "dup", head: "the same amount, the same place, twice", sub: "flagged as a possible duplicate" },
    { cls: "outlier", head: "far bigger than that category usually is", sub: "flagged as unusual, for a second look" },
    { cls: "unread", head: "read off a receipt, but not confidently", sub: "marked to check — the amount is never quietly assumed" },
  ];
  return (
    <>
      <div className="ledger-card ex-ghost" style={{ marginTop: "0.75rem" }} aria-hidden>
        <table className="ledger-table">
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Date</th>
              <th style={{ textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {marks.map((m) => (
              <tr key={m.cls} className={`ledger-row ${m.cls === "dup" ? "flagged" : ""}`}>
                <td colSpan={3}>
                  <div className="ex-ghost-row">
                    <span className={`audit-mark ${m.cls}`} />
                    <span className="ex-ghost-txt">
                      <span className="ex-ghost-head">{m.head}</span>
                      <span className="ex-ghost-sub">{m.sub}</span>
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ex-checks">
        <p><b>Every change is kept.</b> Edit an amount, change a category, mark something not yours —
          the entry carries its own history, so the book can be audited later, by you.</p>
        <p><b>Recurring, ranked by what it costs a year.</b> Anything that repeats is listed by its
          annual cost, so a small weekly habit sits above a pricier monthly one.</p>
      </div>

      <TypedEntry categories={categories} defaultCurrency={defaultCurrency} onAdded={onAdded} />

      <div className="ex-note">
        <span className="ex-note-lbl">Where this lives</span>
        The book is a file on this device, backed up to your <b>own</b> GitHub or Drive when you ask.
        There is no bank connection to break and no subscription — you pay only for the AI steps you
        actually use.
      </div>
    </>
  );
}

// ── THE FREE DOOR — one line, no AI, no cost ────────────────────────────────────
// The receipt is the promise and it is the paid path (on-device OCR, then a billed `structure`
// call). This is the other door: type the amount and what it was for. Pure local write — the same
// `create_expense` the rest of the app uses, which also writes the entry's first audit line.
function TypedEntry({ categories, defaultCurrency, onAdded }: {
  categories: Category[]; defaultCurrency: string; onAdded: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [vendor, setVendor] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const amountN = Number(amount.trim());
  const ready = vendor.trim() !== "" && Number.isFinite(amountN) && amountN > 0;

  async function save() {
    if (!ready || saving) return;
    setSaving(true); setErr(null);
    try {
      const d = new Date();
      const dateOn = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      await store.createExpense({
        vendor: vendor.trim(),
        amount_cents: Math.round(amountN * 100),
        date_on: dateOn,
        currency: defaultCurrency,
        category_id: categoryId || null,
      });
      setAmount(""); setVendor(""); setCategoryId("");
      onAdded();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="ex-typed" aria-label="Write the first line by hand">
      <div className="ex-typed-lbl">Write the first line</div>
      <div className="ex-typed-row">
        <input className="ex-typed-amt" placeholder="Amount" inputMode="decimal" value={amount}
          onChange={(e) => { setAmount(e.target.value.replace(/[^\d.]/g, "")); setErr(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") void save(); }} aria-label="Amount" />
        <input className="ex-typed-vendor" placeholder="What was it for?" value={vendor}
          onChange={(e) => { setVendor(e.target.value); setErr(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") void save(); }} aria-label="What was it for" />
      </div>
      <select className="ex-typed-cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} aria-label="Category (optional)">
        <option value="">Category — optional</option>
        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <div className="ex-typed-hint">Typed by hand, so nothing is sent and nothing is charged. Dated today; change it later if it was not.</div>
      {err && <div className="ex-typed-err">{err}</div>}
      <button className="btn btn-primary ex-typed-save" disabled={!ready || saving} onClick={save}>
        {saving ? "Writing…" : "Write it in"}
      </button>
    </section>
  );
}

function ExportScopeDialog({ format, filtered, monthExpenses, allExpenses, onCancel, onChoose }: {
  format: ExportFormat; filtered: Expense[]; monthExpenses: Expense[]; allExpenses: Expense[];
  onCancel: () => void; onChoose: (set: Expense[]) => void;
}) {
  const label = format === "quickbooks" ? "QuickBooks" : format === "xero" ? "Xero" : "CSV";
  const opt = (text: string, set: Expense[]) => (
    <button className="btn" style={{ width: "100%", textAlign: "left", marginBottom: "0.4rem", display: "flex", justifyContent: "space-between" }} onClick={() => onChoose(set)}>
      <span>{text}</span><span className="muted">{set.length}</span>
    </button>
  );
  return (
    <ModalShell title={`Export to ${label}`} onCancel={onCancel}>
      <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 0.75rem" }}>Choose which expenses to export.</p>
      {opt("Current filter", filtered)}
      {opt("This month", monthExpenses)}
      {opt("All expenses", allExpenses)}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "0.5rem" }}>
        <button className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={onCancel}>Cancel</button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, children, onCancel }: { title: string; children: React.ReactNode; onCancel: () => void }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)", display: "grid", placeItems: "center", zIndex: 50, padding: "1rem" }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 340, padding: "1.25rem", boxShadow: "var(--shadow-2)" }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.75rem" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function Insights({ expenses, categories, monthLabel, monthStart, onAnomalies }: { expenses: Expense[]; categories: Category[]; monthLabel: string; monthStart: string; onAnomalies: (a: Anomaly[]) => void }) {
  const [summary, setSummary] = useState<string | null>(null);
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [showAll, setShowAll] = useState(false);
  // 🔴 ON TAP ONLY (Bogdan 2026-09-15: "only on tap is fine"). The two AI calls below are billed,
  // and they used to fire on every load/reload whenever the month had an expense. Now nothing runs
  // until the customer taps "Summarize my month" — "idle" is the resting state, never auto-run.
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const statusLabels = useStatusLabels();
  // Key = the financially-meaningful shape of the month's rows. Status is deliberately excluded,
  // so approving/rejecting a row keeps the summary. Set ONLY on success → an error never poisons it.
  const lastKey = useRef("");
  const dataKey = useMemo(() => monthStart + "|" + expenses.map((e) => `${e.id}:${e.amount_cents}:${e.category_name ?? ""}:${e.date_on}:${e.vendor}`).join("|"), [expenses, monthStart]);
  // Hold the latest onAnomalies without re-triggering the (billed) run — keeping it out
  // of the deps preserves the P0 cost-cache: AI still fires only when the data signature changes.
  const onAnomaliesRef = useRef(onAnomalies);
  onAnomaliesRef.current = onAnomalies;
  const statusRef = useRef(status);
  statusRef.current = status;

  const run = useCallback(async () => {
    if (expenses.length === 0) { lastKey.current = ""; setSummary(null); setAnomalies([]); setStatus("done"); onAnomaliesRef.current([]); return; }
    const key = dataKey;
    if (key === lastKey.current) { setStatus("done"); return; }
    setStatus("loading");
    // Cold-launch race: the Supabase session restores from async filesystem storage (a Tauri
    // IPC + file read), which on a cold launch can take several seconds — longer than the
    // dashboard takes to render. getAccessToken() is null until then, and a null token would
    // silently no-op the (now-throwing) brain call. Poll generously (bounded ~14s) for it; the
    // onAuthStateChange listener below is the backstop if it's still not ready.
    let token = await getAccessToken();
    for (let i = 0; !token && i < 40; i++) { await new Promise((r) => setTimeout(r, 350)); token = await getAccessToken(); }
    if (!token) { setStatus("error"); return; }
    const label = new Date(monthStart + "T00:00:00Z").toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
    const sText = expenses.map((e) => `${e.date_on} | ${e.vendor} | ${(e.amount_cents / 100).toFixed(2)} | ${e.category_name ?? "Uncategorized"} | ${e.status}`).join("\n");
    const aText = expenses.map((e) => `${e.id} | ${e.date_on} | ${e.vendor} | ${(e.amount_cents / 100).toFixed(2)} | ${e.category_name ?? "Uncategorized"}`).join("\n");
    const ids = new Set(expenses.map((e) => e.id));
    try {
      const [s, a] = await Promise.all([monthSummary(sText, label), expenses.length >= 2 ? findAnomalies(aText, ids) : Promise.resolve([] as Anomaly[])]);
      // A duplicate-charge finding concerns a PAIR, and the model returns it once per side — so the
      // card list showed the same sentence twice ("AWS $312.18 appears 2× (07-15, 07-08)"), which
      // reads like the app is broken. Dedupe the CARDS by the reason text.
      // 🔴 But NOT the ledger flags: both rows of the pair genuinely are implicated and both should
      // stay marked in the table. So onAnomalies still gets the full list — dedupe display only.
      const seenReason = new Set<string>();
      const cards: Anomaly[] = [];
      for (const x of a) {
        const k = x.reason.trim().toLowerCase();
        if (seenReason.has(k)) continue;
        seenReason.add(k);
        cards.push(x);
      }
      setSummary(s); setAnomalies(cards); onAnomaliesRef.current(a); lastKey.current = key; setStatus("done");
    } catch {
      setStatus("error");
    }
  }, [expenses, monthStart, dataKey]);

  // No auto-run. If the month's data changes after a summary was read (new/edited/deleted row, or
  // another month), the old reading is stale: drop it and go back to the tap control — never re-bill.
  useEffect(() => {
    if (lastKey.current && dataKey !== lastKey.current) {
      lastKey.current = ""; setSummary(null); setAnomalies([]); setShowAll(false); setStatus("idle"); onAnomaliesRef.current([]);
    }
  }, [dataKey]);

  // Recover from a cold-launch failure: if the session lands AFTER a TAPPED attempt errored,
  // re-fire once it's available. Guarded by statusRef so it only retries an error (never re-bills
  // a success, never runs untapped — "error" only comes from a tap) and doesn't re-subscribe per status.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session && statusRef.current === "error") void run();
    });
    return () => subscription.unsubscribe();
  }, [run]);

  // Category shape — free, on-device arithmetic. One ruled bar per category, width = share.
  const shape = useMemo(() => {
    const byCat = new Map<string, number>();
    for (const e of expenses) byCat.set(e.category_name ?? "Uncategorised", (byCat.get(e.category_name ?? "Uncategorised") ?? 0) + e.amount_cents);
    const total = [...byCat.values()].reduce((s, v) => s + v, 0) || 1;
    return [...byCat.entries()].sort((a, b) => b[1] - a[1]).map(([name, cents]) => ({
      name, cents, share: cents / total,
      color: categories.find((c) => c.name === name)?.color ?? "var(--accent)",
    }));
  }, [expenses, categories]);

  const unreviewed = expenses.filter((e) => e.status === "pending").length;
  if (expenses.length === 0) return null;
  return (
    <section className="position">
      <div className="eyebrow">This month · {monthLabel}</div>
      <div className="position-figure">{moneyByCurrency(expenses)}</div>
      <div className="position-caption">
        {expenses.length} expense{expenses.length !== 1 ? "s" : ""}{unreviewed > 0 ? ` · ${unreviewed} ${statusLabels.pending}` : ""}{anomalies.length > 0 ? ` · ${anomalies.length} need a second look` : ""}
      </div>
      <div className="shape">
        {shape.map((s) => (
          <div key={s.name} className="shape-row">
            <span className="muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
            <span className="shape-bar"><i style={{ width: `${Math.max(2, Math.round(s.share * 100))}%`, background: s.color }} /></span>
            <span className="tnum">{money(s.cents)}</span>
          </div>
        ))}
      </div>

      <div className="eyebrow" style={{ marginTop: "var(--space-5)" }}>The month, read</div>
      {status === "idle" && (
        <button type="button" className="btn btn-sm" onClick={() => void run()}><Bot size={14} /> Summarize my month</button>
      )}
      {status === "loading" && (
        <div style={{ display: "grid", gap: "0.6rem" }} aria-label="Analyzing this month">
          <div className="skeleton" style={{ width: "92%" }} /><div className="skeleton" style={{ width: "78%" }} /><div className="skeleton" style={{ width: "85%" }} />
        </div>
      )}
      {status === "error" && (
        <div className="row" style={{ gap: "0.5rem", justifyContent: "space-between" }}>
          <span className="row" style={{ gap: "0.4rem", fontSize: "var(--fs-xs)", color: "var(--ink-2)" }}><WifiOff size={14} /> The month’s reading didn’t load.</span>
          <button className="btn btn-sm" onClick={() => void run()}>Retry</button>
        </div>
      )}
      {/* pre-wrap, NOT the default. The model writes the month in short labelled lines; §5c strips
          the markdown that carried the structure, and a plain <p> then collapses every newline into
          one run-on wall. Preserving the model's own line breaks is what makes it readable.
          CLAMPED BY DEFAULT: a full month summary runs longer than the phone screen. */}
      {status === "done" && summary && (
        <>
          <p className={`narration ${showAll ? "" : "narration-clamp"}`} style={{ margin: "0 0 0.35rem" }}>{summary}</p>
          <button type="button" className="link" onClick={() => setShowAll((v) => !v)} style={{ marginBottom: "0.4rem" }}>
            {showAll ? "Show less" : "Read the full summary"}
          </button>
        </>
      )}
      {status === "done" && anomalies.length > 0 && (
        <>
          <div className="eyebrow" style={{ marginTop: "var(--space-4)" }}>Needs a second look</div>
          {anomalies.map((a) => <div key={a.expense_id} className="finding"><AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} /> {a.reason}</div>)}
        </>
      )}
      {(summary || anomalies.length > 0) && <div style={{ marginTop: 8 }}><OrioraBadge /></div>}
    </section>
  );
}

function AddExpense({ categories, editing, onDone, onCancel }: { categories: Category[]; editing: Expense | null; onDone: () => void; onCancel: () => void }) {
  const [vendor, setVendor] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(getDefaultCurrency());
  const [dateOn, setDateOn] = useState(todayISO());
  const [categoryId, setCategoryId] = useState("");
  const [notes, setNotes] = useState("");
  const [receipt, setReceipt] = useState("");
  const [receiptPath, setReceiptPath] = useState<string | null>(null);
  // The photo you just took, openable before you commit the expense — this is the moment you
  // check the AI read the total off the right line.
  const [composerViewer, setComposerViewer] = useState<ViewerPhoto[] | null>(null);
  const [conf, setConf] = useState<number | null>(null);
  const [busy, setBusy] = useState<"" | "read" | "ocr" | "save">("");
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [errCode, setErrCode] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (editing) {
      setVendor(editing.vendor); setAmount((editing.amount_cents / 100).toFixed(2)); setCurrency(editing.currency || getDefaultCurrency()); setDateOn(editing.date_on.slice(0, 10));
      setCategoryId(editing.category_id ?? ""); setNotes(editing.notes ?? ""); setReceiptPath(editing.receipt_path); setConf(editing.ai_confidence);
    }
  }, [editing]);

  function reset() {
    setVendor(""); setAmount(""); setDateOn(todayISO()); setCategoryId(""); setNotes(""); setReceipt(""); setReceiptPath(null); setConf(null); setNote(null);
  }

  function applyStructured(r: { vendor: string; amount_cents: number; date_on: string; currency?: string; category: string; confidence: number }) {
    const m = categories.find((c) => c.name.toLowerCase() === r.category.toLowerCase());
    setVendor(r.vendor); setAmount((r.amount_cents / 100).toFixed(2)); setDateOn(r.date_on); if (m) setCategoryId(m.id);
    // The server already reads the receipt's own currency ("KOKKU EUR" → EUR) — apply it, don't
    // silently keep the default (a €14.54 receipt was landing in the book as $14.54, found in
    // real-use QA 2026-08-27).
    if (r.currency && /^[A-Z]{3}$/.test(r.currency)) setCurrency(r.currency);
    setConf(r.confidence); setNote("AI read your receipt — confirm and save");
  }

  async function readText() {
    if (!receipt.trim()) return;
    setBusy("read"); setErr(null); setErrCode(null); setNote(null);
    try { applyStructured(await structureReceipt(receipt.trim())); }
    catch (e) { setErr(e instanceof BrainError ? e.message : "Could not read that receipt"); setErrCode(e instanceof BrainError ? e.code : null); }
    finally { setBusy(""); }
  }

  // ONE ingest pipeline for all three input gestures (pick / drag-drop / paste):
  // the image is already in receipts/ → on-device OCR (macOS Vision) → AI structure.
  async function ingestStored(stored: string) {
    setReceiptPath(stored);
    // On-device OCR. If unavailable, keep the image + ask for paste/manual.
    try {
      const text = await invoke<string>("ocr_image", { path: stored });
      if (text && text.trim()) applyStructured(await structureReceipt(text.trim()));
      else setNote("Receipt attached — paste its text or fill the form, then save.");
    } catch {
      setNote("Receipt attached — paste its text or fill the form, then save.");
    }
  }

  // From a file path (file picker + drag-drop): copy into receipts/, then ingest.
  async function ingestSrcPath(srcPath: string) {
    setErr(null); setNote(null); setBusy("ocr");
    try { await ingestStored(await store.saveReceiptImage(srcPath)); }
    catch (e) { setErr(e instanceof Error ? e.message : "Could not attach photo"); }
    finally { setBusy(""); }
  }

  // From clipboard bytes (⌘V a screenshot): write to receipts/ via Rust, then ingest.
  async function ingestPastedImage(blob: Blob) {
    setErr(null); setNote(null); setBusy("ocr");
    try {
      const ext = (blob.type.split("/")[1] || "png").replace("jpeg", "jpg");
      const bytes = Array.from(new Uint8Array(await blob.arrayBuffer()));
      await ingestStored(await store.savePastedImage(bytes, ext));
    } catch (e) { setErr(e instanceof Error ? e.message : "Could not attach pasted image"); }
    finally { setBusy(""); }
  }

  // Hidden <input type=file> for iOS. The desktop file dialog (openFileDialog) is a
  // no-op on iPhone/iPad — that's why tapping "Choose photo" did nothing (Apple 2.1(a)).
  const photoInputRef = useRef<HTMLInputElement>(null);

  async function choosePhoto() {
    setErr(null); setNote(null);
    // iOS has no desktop file browser → openFileDialog silently does nothing. Open the
    // native iOS photo/camera sheet via the hidden <input> instead. platform() is sync,
    // so .click() stays inside the tap's user-gesture (required for the sheet to show).
    if (platform() === "ios") { photoInputRef.current?.click(); return; }
    const picked = await openFileDialog({ multiple: false, filters: [{ name: "Image", extensions: ["png", "jpg", "jpeg", "heic", "webp"] }] });
    if (picked && typeof picked === "string") await ingestSrcPath(picked);
  }

  // iOS file-input handler — route the picked File (a Blob) through the SAME OCR ingest
  // path used for pasted screenshots; reset value so re-picking the same image refires.
  function onPhotoInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) void ingestPastedImage(file);
  }

  // Latest-handler refs so the window-global drop/paste listeners register once and never
  // go stale (same pattern as Insights' onAnomalies). Both no-op while editing an expense.
  const onDropRef = useRef<(paths: string[]) => void>(() => {});
  onDropRef.current = (paths) => {
    if (editing) return;
    const img = paths.find((p) => /\.(png|jpe?g|heic|webp)$/i.test(p));
    if (img) void ingestSrcPath(img);
  };
  const onPasteRef = useRef<(blob: Blob) => void>(() => {});
  onPasteRef.current = (blob) => { if (!editing) void ingestPastedImage(blob); };

  // Native file-drop: Tauri delivers file PATHS (and suppresses HTML5 ondrop while the
  // native handler is on), so the drop-zone funnels straight into the same OCR pipeline.
  useEffect(() => {
    let un: (() => void) | null = null;
    void getCurrentWebview().onDragDropEvent((event) => {
      const p = event.payload;
      if (p.type === "enter" || p.type === "over") setDragOver(true);
      else if (p.type === "leave") setDragOver(false);
      else if (p.type === "drop") { setDragOver(false); onDropRef.current(p.paths); }
    }).then((u) => { un = u; });
    return () => { if (un) un(); };
  }, []);

  // ⌘V a screenshot anywhere in the window → ingest the clipboard image (text paste unaffected).
  useEffect(() => {
    const onPaste = (ev: ClipboardEvent) => {
      const items = ev.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          const blob = items[i].getAsFile();
          if (blob) { ev.preventDefault(); onPasteRef.current(blob); }
          return;
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  async function suggest() {
    if (!vendor.trim()) return; setErr(null); setErrCode(null);
    try {
      const r = await suggestCategory({ vendor: vendor.trim(), amount_cents: Math.round((parseFloat(amount) || 0) * 100), notes, categories: categories.map((c) => c.name) });
      const m = categories.find((c) => c.name.toLowerCase() === r.category.toLowerCase()); if (m) setCategoryId(m.id);
    } catch (e) { setErr(e instanceof BrainError ? e.message : "Could not suggest"); setErrCode(e instanceof BrainError ? e.code : null); }
  }

  async function save(ev: React.FormEvent) {
    ev.preventDefault(); setBusy("save"); setErr(null);
    const amount_cents = Math.round((parseFloat(amount) || 0) * 100);
    try {
      if (editing) {
        await store.updateExpense(editing.id, { vendor, amount_cents, currency, date_on: dateOn, category_id: categoryId, notes });
      } else {
        await store.createExpense({ vendor, amount_cents, currency, date_on: dateOn, category_id: categoryId || null, notes, ai_confidence: conf, receipt_path: receiptPath });
      }
      reset(); onDone();
    } catch (e) { setErr(e instanceof Error ? e.message : "Could not save"); } finally { setBusy(""); }
  }

  // R1 — capture leads. The manual/typed half unfolds beneath the shutter only when asked for
  // ("Type it"), when a receipt has been read (confirm the AI's fields), or while editing.
  const [typedOpen, setTypedOpen] = useState(false);
  const formVisible = !!(editing || typedOpen || receiptPath || note || vendor || receipt);

  return (
    <section className={`capture ${dragOver ? "dragover" : ""}`}>
      {editing && (
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.75rem" }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>Edit expense</div>
          <button className="btn btn-sm" onClick={() => { reset(); setTypedOpen(false); onCancel(); }}>Cancel edit</button>
        </div>
      )}

      {!editing && (
        <>
          <button type="button" className="capture-shutter" onClick={choosePhoto} disabled={!!busy}>
            <img src={glyphCapture} alt="" aria-hidden="true" width={40} height={40} style={{ flexShrink: 0 }} />
            <span>
              <span className="capture-title" style={{ display: "block" }}>{busy === "ocr" ? "Reading the receipt…" : "Photograph a receipt"}</span>
              <span className="capture-sub" style={{ display: "block" }}>{dragOver ? "Drop the receipt to read it" : "Read on this device, structured in one pass"}</span>
            </span>
          </button>
          <div className="capture-alts">
            <button type="button" className="btn" onClick={choosePhoto} disabled={!!busy}><Camera size={14} /> Library</button>
            <button type="button" className="btn" onClick={() => setTypedOpen((v) => !v)} aria-expanded={formVisible}>Type it</button>
          </div>
          {/* §7b-verified: receipt IMAGE FILES live in receipts/ and are excluded from Cloud Backup —
              backup.rs dumps SQLite tables only (SELECT * FROM user tables), never the image blobs on
              disk. Only the OCR'd TEXT of a receipt is sent per call. Checked 2026-08-06. */}
          <div className="capture-hint" data-note="§7b-verified: backup.rs dumps SQLite tables only; receipt image files never leave the device"><Lock size={11} style={{ flexShrink: 0 }} /> Receipts stay on this device. Per-call text passes through Oriora, which doesn’t retain it.</div>
          {/* Billing cue at the door, not buried in the account sheet: reading a receipt IS the
              billed call, and this is the control that fires it. Same shape as the fleet's other
              cues (KnowCode's "charged per ask on your own key — no subscription", Scout's run
              trail). Its own line so the §7b privacy literal above stays byte-identical. */}
          <div className="capture-hint">Reading a receipt is one AI call — pay-per-use from your Oriora wallet, no subscription.</div>
          <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoInputChange} style={{ display: "none" }} />
        </>
      )}

      {!editing && formVisible && (
        <div style={{ marginTop: "var(--space-4)" }}>
          <textarea rows={2} placeholder={platform() === "ios" ? "…or paste receipt text" : "…or paste receipt text (Mac Live Text)"} value={receipt} onChange={(e) => setReceipt(e.target.value)} style={{ fontFamily: "var(--mono)", fontSize: "var(--fs-xs)", marginBottom: "0.5rem" }} />
          <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
            <button type="button" className="btn btn-sm" onClick={readText} disabled={!!busy || !receipt.trim()}><Bot size={14} /> {busy === "read" ? "Reading…" : "Read text"}</button>
            {receiptPath ? (
              <button type="button" className="row" title="Open receipt full size" aria-label="Open receipt full size"
                style={{ gap: "0.4rem", background: "var(--elev-2)", border: "1px solid var(--border)", borderRadius: 8, padding: "0.15rem 0.4rem", cursor: "zoom-in" }}
                onClick={() => setComposerViewer([{ src: convertFileSrc(receiptPath), alt: "Receipt you just photographed" }])}>
                <img src={convertFileSrc(receiptPath)} alt="receipt" style={{ width: 28, height: 28, objectFit: "cover", borderRadius: 4 }} />
                <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>receipt attached</span>
              </button>
            ) : null}
          </div>
          {note && <div className="row" style={{ fontSize: "var(--fs-xs)", color: "var(--accent)", marginBottom: "0.5rem", gap: "0.3rem" }}><Check size={14} /> {note}</div>}
        </div>
      )}
      {composerViewer && <ReceiptViewer photos={composerViewer} onClose={() => setComposerViewer(null)} />}

      {(editing || formVisible) && (
      <form onSubmit={save} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: "0.6rem" }}>
        <input placeholder="Vendor *" required value={vendor} onChange={(e) => setVendor(e.target.value)} />
        <input placeholder="Amount *" type="number" step="0.01" min="0" required value={amount} onChange={(e) => setAmount(e.target.value)} />
        <select aria-label="Currency" title="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ width: "auto" }}>
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="date" required value={dateOn} onChange={(e) => setDateOn(e.target.value)} />
        <div className="row" style={{ gap: "0.3rem" }}>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Category</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button type="button" className="btn" title="AI suggest" style={{ padding: "0.45rem 0.6rem", display: "inline-flex", alignItems: "center" }} onClick={suggest}><Sparkles size={16} /></button>
        </div>
        <input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} style={{ gridColumn: "1 / 3" }} />
        <div className="row" style={{ gridColumn: "1 / 3", justifyContent: "space-between" }}>
          <span style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", display: "inline-flex", alignItems: "center", gap: 6 }}>
            {err}
            {errCode === "insufficient_funds" && (
              <button type="button" className="btn" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0.55rem" }} onClick={() => void invoke("open_external", { url: ACCOUNT_URL })}>Open Oriora account</button>
            )}
          </span>
          {/* FULL WIDTH, deliberately. This row is `space-between`, which pinned the primary action
              to the bottom-RIGHT — the exact corner the fixed .ecp-launch "Chat" pill occupies, so
              the pill sat on top of "Save expense" and a tap meant to save opened the chat instead.
              (Reserving page padding does NOT fix this: the launcher is position:fixed and floats
              over the viewport, not the end of the document.) Full width keeps most of the button
              clear of the pill, so the action is always reachable. */}
          <button type="submit" className="btn btn-primary" disabled={!!busy} style={{ width: "100%", minHeight: 48 }}>{busy === "save" ? "Saving…" : editing ? "Update" : "Enter in the book"}</button>
        </div>
      </form>
      )}
    </section>
  );
}

function AskExpenses({ askInputRef, categories, expenses, onApplyFilter }: { askInputRef?: React.Ref<HTMLInputElement>; categories: Category[]; expenses: Expense[]; onApplyFilter: (filter: ExpenseFilter, label: string) => void }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  // F3 fix: this used to be a `useState` SNAPSHOT of the matching rows, computed once inside
  // run() and never recomputed — so an expense deleted after asking still showed here until the
  // next full ask (or a reload). Storing only the FILTER and re-deriving the match list from the
  // live `expenses` prop on every render means a delete (which updates `expenses` upstream) is
  // reflected immediately, with no re-ask needed.
  const [lastFilter, setLastFilter] = useState<ExpenseFilter | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [errCode, setErrCode] = useState<string | null>(null);

  const res = useMemo(() => {
    if (!lastFilter) return null;
    const m = applyFilter(expenses, lastFilter);
    return { count: m.length, total: m.reduce((s, e) => s + e.amount_cents, 0), rows: m.slice(0, 100) };
  }, [lastFilter, expenses]);

  async function run(ev: React.FormEvent) {
    ev.preventDefault(); if (!q.trim()) return;
    setBusy(true); setErr(null); setErrCode(null); setLastFilter(null);
    try {
      const filter = await parseExpenseQuery(q.trim(), categories.map((c) => c.name), todayISO());
      setLastFilter(filter);
      onApplyFilter(filter, q.trim());
    } catch (e) { setErr(e instanceof BrainError ? e.message : "Query failed"); setErrCode(e instanceof BrainError ? e.code : null); } finally { setBusy(false); }
  }

  return (
    <div>
      <form onSubmit={run} className="book-ask">
        <span className="row" style={{ color: "var(--accent)", flex: "none" }}><Sparkles size={16} /></span>
        <input ref={askInputRef} placeholder='Ask: "travel over $500 this quarter"' value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy} style={{ borderRadius: 999, flex: "none" }}>{busy ? "Asking…" : "Ask"}</button>
      </form>
      {err && (
        <div className="row muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6, gap: 6 }}>
          {err}
          {errCode === "insufficient_funds" && (
            <button type="button" className="btn" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0.55rem" }} onClick={() => void invoke("open_external", { url: ACCOUNT_URL })}>Open Oriora account</button>
          )}
        </div>
      )}
      {res && (
        <div style={{ marginTop: "0.6rem" }}>
          <div style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", marginBottom: 4 }}><b>{res.count}</b> match{res.count !== 1 ? "es" : ""} · {moneyByCurrency(res.rows)}</div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {res.rows.map((e) => (
              <div key={e.id} className="row" style={{ justifyContent: "space-between", fontSize: "var(--fs-xs)", color: "var(--ink-2)", padding: "0.3rem 0", borderBottom: "1px solid var(--border)" }}>
                <span>{e.date_on} · {e.vendor} <span className="muted">({e.category_name ?? "—"})</span></span>
                <span style={{ fontWeight: 500 }}>{money(e.amount_cents, e.currency)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Budgets — a limit the person sets for THEMSELVES, per category or overall.
 * Entirely on-device arithmetic: no AI call, nothing sent anywhere, so it costs nothing to use.
 */
function Budgets({ categories, monthStart, monthLabel, reloadKey }: { categories: Category[]; monthStart: string; monthLabel: string; reloadKey: number }) {
  const [rows, setRows] = useState<BudgetStatus[]>([]);
  const [catId, setCatId] = useState<string>("");
  const [amount, setAmount] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => {
    store.budgetStatus(monthStart).then(setRows).catch(() => setRows([]));
  }, [monthStart]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const add = async (ev: FormEvent) => {
    ev.preventDefault();
    setErr(null);
    const cents = Math.round(parseFloat(amount.replace(/,/g, "")) * 100);
    if (!Number.isFinite(cents) || cents <= 0) { setErr("Enter an amount above zero"); return; }
    try {
      await store.setBudget(catId || null, cents);
      setAmount("");
      load();
    } catch (e) { setErr(e instanceof Error ? e.message : "Could not save that budget"); }
  };

  return (
    <div className="card" style={{ padding: "1rem 1.25rem", marginBottom: "0.75rem" }}>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.2rem" }}>Budgets</div>
      <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: "0.7rem" }}>
        What you meant to spend in {monthLabel}, against what you actually did. Worked out on this device — no AI, nothing sent.
      </div>

      <form className="row" style={{ gap: "0.4rem", marginBottom: "0.7rem", flexWrap: "wrap" }} onSubmit={add}>
        <select value={catId} onChange={(e) => setCatId(e.target.value)} aria-label="Budget for">
          <option value="">Everything</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input inputMode="decimal" placeholder="Limit, e.g. 250" value={amount} onChange={(e) => setAmount(e.target.value)} required style={{ maxWidth: 150 }} aria-label="Monthly limit" />
        <button type="submit" className="btn btn-primary" style={{ fontSize: "var(--fs-xs)" }}>Set budget</button>
      </form>
      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: 6 }}>{err}</div>}

      {rows.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>
          No budgets yet — set one above and this month's spending fills it in.
        </div>
      ) : (
        <div style={{ display: "grid", gap: "0.55rem" }}>
          {rows.map((b) => {
            const over = b.remaining_cents < 0;
            return (
              <div key={b.id}>
                <div className="row" style={{ justifyContent: "space-between", fontSize: "var(--fs-xs)", marginBottom: 3 }}>
                  <span className="row" style={{ gap: "0.35rem" }}>
                    {b.category_color && <span style={{ width: 9, height: 9, borderRadius: "50%", background: b.category_color }} />}
                    <strong>{b.category_name ?? "Everything"}</strong>
                  </span>
                  <span className="tnum" style={{ color: over ? "var(--rose)" : undefined }}>
                    {money(b.spent_cents)} of {money(b.amount_cents)}
                    {" · "}
                    {over ? `over by ${money(-b.remaining_cents)}` : `${money(b.remaining_cents)} left`}
                  </span>
                </div>
                <div style={{ height: 6, borderRadius: 999, background: "var(--elev-2)", overflow: "hidden" }}
                     role="progressbar" aria-valuenow={b.percent_used} aria-valuemin={0} aria-valuemax={100}
                     aria-label={`${b.category_name ?? "Everything"} budget used`}>
                  <div style={{ width: `${b.percent_used}%`, height: "100%", background: over ? "var(--rose)" : "var(--accent)" }} />
                </div>
                <div className="row" style={{ justifyContent: "flex-end", marginTop: 2 }}>
                  <button className="btn" style={{ border: "none", background: "none", padding: 0, fontSize: "var(--fs-xs)", color: "var(--rose)" }}
                    onClick={async () => { await store.deleteBudget(b.id); load(); }}>Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * "Where did my money go" — the category split for the month plus a month-by-month strip.
 * The split was already computed for the AI summary and thrown away; this shows it. No AI call.
 */
function WhereItWent({ monthStart, monthLabel, reloadKey }: { monthStart: string; monthLabel: string; reloadKey: number }) {
  const [split, setSplit] = useState<{ category_name: string | null; category_color: string | null; total_cents: number; count: number }[]>([]);
  const [trend, setTrend] = useState<MonthTotal[]>([]);
  useEffect(() => {
    const end = `${monthStart.slice(0, 7)}-31`;
    store.spendByCategory(monthStart, end).then(setSplit).catch(() => setSplit([]));
    store.spendTrend(6).then(setTrend).catch(() => setTrend([]));
  }, [monthStart, reloadKey]);

  const total = split.reduce((s, r) => s + r.total_cents, 0);
  const peak = Math.max(1, ...trend.map((t) => t.total_cents));

  return (
    <div className="card" style={{ padding: "1rem 1.25rem", marginBottom: "0.75rem" }}>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.2rem" }}>Where it went</div>
      <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: "0.7rem" }}>{monthLabel}, biggest first.</div>

      {total === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>Nothing logged for {monthLabel} yet.</div>
      ) : (
        <div style={{ display: "grid", gap: "0.5rem" }}>
          {split.map((r, i) => {
            const pct = Math.round((r.total_cents / total) * 100);
            return (
              <div key={`${r.category_name ?? "none"}-${i}`}>
                <div className="row" style={{ justifyContent: "space-between", fontSize: "var(--fs-xs)", marginBottom: 3 }}>
                  <span className="row" style={{ gap: "0.35rem" }}>
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: r.category_color ?? "var(--elev-2)" }} />
                    {r.category_name ?? "Uncategorised"}
                    <span className="muted">· {r.count}</span>
                  </span>
                  <span className="tnum">{money(r.total_cents)} · {pct}%</span>
                </div>
                <div style={{ height: 6, borderRadius: 999, background: "var(--elev-2)", overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: r.category_color ?? "var(--accent)" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {trend.length > 1 && (
        <>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-xs)", margin: "0.9rem 0 0.4rem" }}>Last {trend.length} months</div>
          <div className="row" style={{ gap: "0.5rem", alignItems: "flex-end", height: 60 }}>
            {[...trend].reverse().map((t) => (
              <div key={t.month} style={{ flex: 1, textAlign: "center" }} title={`${t.month}: ${money(t.total_cents)} across ${t.count}`}>
                <div style={{ height: Math.max(3, Math.round((t.total_cents / peak) * 44)), background: "var(--accent)", borderRadius: 3, opacity: 0.85 }} />
                <div className="muted" style={{ fontSize: "10px", marginTop: 3 }}>{t.month.slice(5)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The three status words, in the customer's own vocabulary.
 *
 * Everyone sorts money differently — "not mine" is "refunded" to one person, "business" to
 * another. The stored values never change, so relabelling is cosmetic: exports, backups and
 * restores are unaffected.
 */
function StatusLabelEditor() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Record<ExpenseStatus, string>>(loadStatusLabels);
  const current = useStatusLabels();
  const isCustom = (Object.keys(DEFAULT_STATUS_LABELS) as ExpenseStatus[]).some(
    (k) => current[k] !== DEFAULT_STATUS_LABELS[k],
  );

  if (!open) {
    return (
      <div className="row" style={{ gap: "0.4rem", marginBottom: "0.6rem", fontSize: "var(--fs-xs)" }}>
        <span className="muted">Labels: {current.pending} · {current.approved} · {current.rejected}</span>
        <button className="btn" style={{ border: "none", background: "none", padding: 0, color: "var(--accent)", fontSize: "var(--fs-xs)" }}
          onClick={() => { setDraft(loadStatusLabels()); setOpen(true); }}>
          Use my own words
        </button>
      </div>
    );
  }

  return (
    <form className="row" style={{ gap: "0.35rem", marginBottom: "0.6rem", flexWrap: "wrap" }}
      onSubmit={(ev) => { ev.preventDefault(); saveStatusLabels(draft); setOpen(false); }}>
      {(Object.keys(DEFAULT_STATUS_LABELS) as ExpenseStatus[]).map((k) => (
        <input key={k} value={draft[k]} maxLength={24} required
          aria-label={`Label for ${DEFAULT_STATUS_LABELS[k]}`}
          placeholder={DEFAULT_STATUS_LABELS[k]}
          onChange={(ev) => setDraft({ ...draft, [k]: ev.target.value })}
          style={{ maxWidth: 108, fontSize: "var(--fs-xs)" }} />
      ))}
      <button type="submit" className="btn btn-primary" style={{ fontSize: "var(--fs-xs)" }}>Save labels</button>
      {isCustom && (
        <button type="button" className="btn" style={{ fontSize: "var(--fs-xs)" }}
          onClick={() => { saveStatusLabels({ ...DEFAULT_STATUS_LABELS }); setOpen(false); }}>Reset</button>
      )}
      <button type="button" className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={() => setOpen(false)}>Cancel</button>
    </form>
  );
}

function Categories({ categories, onChange }: { categories: Category[]; onChange: () => void }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#6B7280");
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftColor, setDraftColor] = useState("#6B7280");
  return (
    <div className="card" style={{ padding: "1rem 1.25rem", marginBottom: "0.75rem" }}>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.6rem" }}>Categories</div>
      <form className="row" style={{ gap: "0.4rem", marginBottom: "0.6rem" }}
        onSubmit={async (e) => { e.preventDefault(); setErr(null); try { await store.createCategory(name, color); setName(""); onChange(); } catch (er) { setErr(er instanceof Error ? er.message : "failed"); } }}>
        <input placeholder="New category" value={name} onChange={(e) => setName(e.target.value)} required />
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 44, padding: 2 }} />
        <button type="submit" className="btn btn-primary" style={{ fontSize: "var(--fs-xs)" }}>Add</button>
      </form>
      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: 6 }}>{err}</div>}
      <StatusLabelEditor />
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
        {categories.map((c) => (
          editing === c.id ? (
            // Rename + recolour, defaults included — "I can't rename the categories" is the
            // third-most-common complaint about the apps people pay for in this category.
            <form key={c.id} className="row" style={{ gap: "0.35rem" }}
              onSubmit={async (ev) => {
                ev.preventDefault(); setErr(null);
                try { await store.updateCategory(c.id, { name: draftName, color: draftColor }); setEditing(null); onChange(); }
                catch (er) { setErr(er instanceof Error ? er.message : "failed"); }
              }}>
              <input value={draftName} onChange={(e) => setDraftName(e.target.value)} required autoFocus aria-label={`Rename ${c.name}`} style={{ maxWidth: 130 }} />
              <input type="color" value={draftColor} onChange={(e) => setDraftColor(e.target.value)} style={{ width: 40, padding: 2 }} aria-label={`Colour for ${c.name}`} />
              <button type="submit" className="btn btn-primary" style={{ fontSize: "var(--fs-xs)" }}>Save</button>
              <button type="button" className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={() => setEditing(null)}>Cancel</button>
            </form>
          ) : (
          <span key={c.id} className="row" style={{ gap: "0.35rem", fontSize: "var(--fs-xs)", background: "var(--elev-2)", borderRadius: 999, padding: "0.2rem 0.6rem" }}>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: c.color }} />{c.name}
            <button className="btn" style={{ padding: "0 0.3rem", border: "none", background: "none", color: "var(--muted)", display: "inline-flex", alignItems: "center" }}
              title={`Rename ${c.name}`} aria-label={`Rename ${c.name}`}
              onClick={() => { setEditing(c.id); setDraftName(c.name); setDraftColor(c.color); }}><Pencil size={11} /></button>
            {!c.is_default && <button className="btn" style={{ padding: "0 0.3rem", border: "none", background: "none", color: "var(--rose)", display: "inline-flex", alignItems: "center" }} title="Remove"
              onClick={async () => { setErr(null); try { await store.deleteCategory(c.id); onChange(); } catch (er) { setErr(er instanceof Error ? er.message : "failed"); } }}><X size={12} /></button>}
          </span>
          )
        ))}
      </div>
    </div>
  );
}

function ExpenseRow({ e, flagged, onEdit, onChange, onViewReceipt }: { e: Expense; flagged: boolean; onEdit: () => void; onChange: () => void; onViewReceipt: () => void }) {
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState<AuditEntry[] | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const statusLabels = useStatusLabels();
  const lowConf = e.ai_confidence !== null && e.ai_confidence < 0.6;
  async function setStatus(status: ExpenseStatus) { setBusy(true); await store.updateExpense(e.id, { status }); onChange(); setBusy(false); }
  async function remove() { setConfirmDelete(false); setBusy(true); await store.deleteExpense(e.id); onChange(); }
  async function toggleAudit() { if (audit) { setAudit(null); return; } setAudit(await store.listAudit(e.id, 50)); }
  return (
    <>
      <tr className={`ledger-row${flagged ? " flagged" : ""}${e.status === "rejected" ? " rejected" : ""}${lowConf ? " low-conf" : ""}`}>
        <td>
          <div className="row" style={{ gap: "0.6rem", minWidth: 0, flexWrap: "nowrap" }}>
            {/* Evidence — the receipt is a first-class column; no receipt = a drawn gap, not a blank. */}
            {e.receipt_path ? (
              <button type="button" className="evidence" title="View receipt" aria-label="View receipt" onClick={onViewReceipt}>
                <img src={convertFileSrc(e.receipt_path)} alt={`Receipt — ${e.vendor}`} />
              </button>
            ) : (
              <button type="button" className="evidence evidence-empty" title="No receipt — edit to attach" aria-label="No receipt — edit to attach" onClick={onEdit}>
                <Camera size={14} style={{ color: "var(--ink-3)" }} />
              </button>
            )}
            <div style={{ minWidth: 0 }}>
              <div className="ledger-vendor">{e.vendor}</div>
              <div className="ledger-meta">{fmtDate(e.date_on)} · {e.category_name ?? "—"}{e.ai_confidence !== null && !lowConf ? " · AI read" : ""}</div>
              {flagged && <span className="audit-mark outlier" title="AI flagged this — review"> needs a second look</span>}
              {!flagged && lowConf && <span className="audit-mark unread" title="AI low confidence — verify"> read at {Math.round((e.ai_confidence ?? 0) * 100)}% — check the paper</span>}
            </div>
          </div>
        </td>
        <td style={{ color: "var(--ink-2)", whiteSpace: "nowrap" }}>{fmtDate(e.date_on)}</td>
        <td style={{ color: "var(--ink-2)" }}>{e.category_name ?? <span className="muted">—</span>}</td>
        <td className={`ledger-amount${lowConf ? " low-conf" : ""}`} title={lowConf ? "AI low confidence — verify" : undefined}>
          {money(e.amount_cents, e.currency)}
        </td>
        <td><span className={`badge ${e.status}`}>{statusLabels[e.status] ?? e.status}</span></td>
        <td>
          <div className="ledger-actions">
            {e.status !== "approved" && <button disabled={busy} className="btn-icon" style={{ color: "var(--accent)" }} title="Mark reviewed" aria-label="Mark expense reviewed" onClick={() => setStatus("approved")}><Check size={14} /></button>}
            {e.status !== "rejected" && <button disabled={busy} className="btn-icon" style={{ color: "var(--rose)" }} title="Flag" aria-label="Flag expense" onClick={() => setStatus("rejected")}><X size={14} /></button>}
            <button className="btn-icon" title="Edit" aria-label="Edit expense" onClick={onEdit}><Pencil size={13} /></button>
            <button className="btn-icon" onClick={toggleAudit} title="Audit trail" aria-label="Audit trail"><History size={14} /></button>
            <button disabled={busy} className="btn-icon" style={{ color: "var(--ink-3)" }} title="Delete" aria-label="Delete expense" onClick={() => setConfirmDelete(true)}><Trash2 size={14} /></button>
          </div>
        </td>
      </tr>
      {audit && (
        <tr>
          <td colSpan={6} style={{ padding: "0 1rem 0.6rem 1rem", background: "var(--elev-2)" }}>
            {audit.map((a) => <div key={a.id} className="muted" style={{ fontSize: "var(--fs-xs)" }}>{fmtDate(a.at)} · {a.action}{a.detail ? ` (${a.detail})` : ""}</div>)}
          </td>
        </tr>
      )}
      {confirmDelete && (
        <tr>
          <td colSpan={6} style={{ padding: 0 }}>
            <ModalShell title="Delete expense?" onCancel={() => setConfirmDelete(false)}>
              <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 1rem" }}>
                Delete <b>{e.vendor}</b> ({money(e.amount_cents, e.currency)})? This also removes its receipt and can’t be undone.
              </p>
              <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
                <button className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={() => setConfirmDelete(false)}>Cancel</button>
                <button className="btn btn-primary" style={{ fontSize: "var(--fs-xs)", background: "var(--rose)", borderColor: "var(--rose)" }} onClick={remove}>Delete</button>
              </div>
            </ModalShell>
          </td>
        </tr>
      )}
    </>
  );
}
